import { createApi } from '@reduxjs/toolkit/query/react';
import {
  CheckoutResponse,
  CreateTrialResponse,
  DownloadShapefilesResponse,
  PurchaseResponse,
} from 'types/createTrial.type';
import { DefaultAPIResponse } from 'types/global.type';
import {
  FieldsResponse,
  FieldTrialsResponse,
  LSDPercentageResponse,
  SingleTrialResponse,
} from 'types/workspace.type';
import { getBaseQuery } from 'utils/config';

const workspaceApi = createApi({
  reducerPath: 'workspaceApi',
  baseQuery: getBaseQuery('workspace'),
  endpoints: (builder) => ({
    fields: builder.query<FieldsResponse, void>({
      query: () => '/fields',
    }),
    fieldTrials: builder.query<FieldTrialsResponse, string>({
      query: (fieldId) => `/fields/${fieldId}/trials`,
    }),
    fieldTrial: builder.query<SingleTrialResponse, string>({
      query: (trialId) => `/trials/${trialId}`,
    }),
    downloadShapefiles: builder.query<DownloadShapefilesResponse, string>({
      query: (trialId) => `/trials/${trialId}/download`,
    }),
    deleteTrial: builder.mutation<DefaultAPIResponse, string>({
      query: (trialId) => ({
        url: `/trials/${trialId}`,
        method: 'DELETE',
      }),
    }),
    createTrial: builder.mutation<CreateTrialResponse, Record<string, any>>({
      query: (body) => ({
        url: '/trials/save',
        body,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    copyTrial: builder.mutation<CreateTrialResponse, Record<string, any>>({
      query: (body) => ({
        url: '/copy-trial',
        body,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    partnerPurchase: builder.query<PurchaseResponse, Record<string, any>>({
      query: (body) => ({
        url: `/trials/checkout/partner`,
        body,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    spendTrialCredit: builder.query<PurchaseResponse, Record<string, any>>({
      query: (body) => ({
        url: '/trials/checkout/trial_credit',
        body,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    checkout: builder.query<
      CheckoutResponse,
      {
        type: String;
        formData: Record<string, any>;
      }
    >({
      query: ({ type, formData }) => ({
        url: `/trials/checkout/${type}`,
        body: formData,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    computeLsdPercentage: builder.mutation<
      LSDPercentageResponse,
      Record<string, any>
    >({
      query: (body) => ({
        url: '/trials/lsd',
        body,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
  }),
});

export const {
  useFieldsQuery,
  useFieldTrialsQuery,
  useFieldTrialQuery,
  useLazyFieldTrialQuery,
  useLazyDownloadShapefilesQuery,
  useCreateTrialMutation,
  useCopyTrialMutation,
  useDeleteTrialMutation,
  useLazyPartnerPurchaseQuery,
  useLazySpendTrialCreditQuery,
  useLazyCheckoutQuery,
  useComputeLsdPercentageMutation,
} = workspaceApi;

export default workspaceApi;
